import { useState } from "react";
import Tabs from "../Tabs";
import UseCaseSummary from "../../pages/Chat/ChatTabs/UseCaseSummary";
import IntendedUseForm from "../../pages/Chat/ChatTabs/IntendedUse";
import BusinessContextForm from "../../pages/Chat/ChatTabs/BusinessContext";
import UseCaseDataContextForm from "../../pages/Chat/ChatTabs/DataContext";
import SystemForm from "../../pages/Chat/ChatTabs/SystemFile";
import CustomButton from "../Button";

import UseCaseChatStore from "../../stores/UseCaseChatStore";
import { AuthStore } from "../../stores";
import RiskPlusReportStore from "../../stores/RiskPlusReportStore";
import ImpactPlusReportStore from "../../stores/ImpactPlusReportStore";
import { DashboardStore } from "../../stores/DashboardStore";
import ReusableModal from "../Modal";
import type { UseCaseChat } from "../../entities/useCaseChat/UseCaseChat";
import { toast } from "react-toastify";

const tabs = ["Summary", "Intended Use", "Context", "Data", "System"];

interface RiskPlusReportConfirmModalProps {
  useCaseChat: UseCaseChat;
  mode: "view" | "generate";
  reportType?: "risk" | "impact";
  isRefresh?: boolean;
  isOpen: boolean;
  onConfirmGeneration: () => void;
  onClose: () => void;
}

const noop = () => { };

export default function RiskPlusReportConfirmModal({
  useCaseChat,
  mode,
  reportType = "risk",
  isRefresh = false,
  isOpen,
  onConfirmGeneration,
  onClose,
}: RiskPlusReportConfirmModalProps) {
  const [activeTab, setActiveTab] = useState("Summary");
  const riskReportCredits = AuthStore.useAuthStore((state) => state.license.riskPlusReportCredits);
  const riskRefreshCredits = AuthStore.useAuthStore((state) => state.license.riskPlusReportRefreshCredits);
  const impactReportCredits = AuthStore.useAuthStore((state) => state.license.impactPlusReportCredits);
  const impactRefreshCredits = AuthStore.useAuthStore((state) => state.license.impactPlusReportRefreshCredits);
  const reportCredits = reportType === "impact" ? impactReportCredits : riskReportCredits;
  const refreshCredits = reportType === "impact" ? impactRefreshCredits : riskRefreshCredits;
  const credits = isRefresh ? refreshCredits : reportCredits;
  const generateRiskPlusReport = RiskPlusReportStore.useRiskPlusReportStore((state) => state.generateRiskPlusReport);
  const generateImpactPlusReport = ImpactPlusReportStore.useImpactPlusReportStore((state) => state.generateImpactPlusReport);
  const useCaseChatDataIsLoading = UseCaseChatStore.useUseCaseChatStore((state) => state.useCaseChatIsLoading);
  const useCaseBriefs = DashboardStore.useDashboardStore((state) => state.useCaseBriefs);

  const handleGenerateReportConfirm = async () => {
    onConfirmGeneration();
    try {
      if (reportType === "impact") {
        await generateImpactPlusReport(useCaseChat.id);
      } else {
        await generateRiskPlusReport(useCaseChat.id);
      }
    } catch (err) {
      console.error("Generate report failed:", err);
      const message = err instanceof Error && /insufficient/i.test(err.message)
        ? "Insufficient credits: report tokens are not available."
        : "Failed to generate report. Please try again.";
      toast.error(message);
    }
  };

  const isGenerating = (): boolean => {
    const brief = useCaseBriefs.find((brief) => brief.useCaseChatId === useCaseChat.id);
    return brief ? brief.jobIsActive : false;
  }

  const modalTitle =
    mode === "generate"
      ? reportType === "impact"
        ? "Generate Impact+ Report"
        : "Generate Risk+ Report"
      : "View Inputs";

  return (
    <ReusableModal
      title={modalTitle}
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      scrollBehavior="inside"
    >

      <div className="flex flex-col max-h-[70vh]">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {modalTitle}
        </h2>
        <div className="shrink-0 mb-4">
          <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto pr-2 space-y-4">
          {activeTab === "Summary" && (
            <UseCaseSummary
              formValues={useCaseChat.useCase.useCaseSummary}
              onInputChange={noop}
              viewOnly={true}
            />
          )}
          {activeTab === "Intended Use" && (
            <IntendedUseForm
              formValues={useCaseChat.useCase.intendedUse}
              onInputChange={noop}
              viewOnly={true}
            />
          )}
          {activeTab === "Context" && (
            <BusinessContextForm
              formValues={useCaseChat.useCase.businessContext}
              onInputChange={noop}
              viewOnly={true}
            />
          )}
          {activeTab === "Data" && (
            <UseCaseDataContextForm
              formValues={useCaseChat.useCase.dataContext}
              onInputChange={noop}
              viewOnly={true}
            />
          )}
          {activeTab === "System" && (
            <SystemForm
              formValues={useCaseChat.useCase.systemComponents}
              onInputChange={noop}
              viewOnly={true}
            />
          )}
        </div>
        {mode === "generate" && (
          <div className="shrink-0 pt-4 mt-4 border-t border-gray-200 flex flex-col gap-3">
            <CustomButton
              type="button"
              color="primary"
              className="rounded-xl px-4 py-2 text-white w-full sm:w-auto"
              onPress={handleGenerateReportConfirm}
              disabled={useCaseChatDataIsLoading || isGenerating()}
            >
              {isGenerating() ? "Generating…" : (isRefresh ? "Refresh Report for 1 Credit" : "Generate Report for 1 Credit")}
            </CustomButton>
            <p className="text-sm text-gray-600">
              {credits != null
                ? `${credits} ${isRefresh ? "refresh" : "report"} credits remaining`
                : `… ${isRefresh ? "refresh" : "report"} credits remaining`}
            </p>
          </div>
        )}
      </div>
    </ReusableModal>
  );
}
